import { useSelector } from 'react-redux';
import { IGlobalState } from '../../../model/IGlobalState';

// UI only for now (user decision 2026-09-24): Client settings phase 1 has no api calls. Everything below is the spec's
// placeholder content; only the client's legal identity (name, ticker, exchange) and website come from state.currentStock.

export const NOT_CONNECTED = 'Preview only: this isn’t connected yet, so nothing was changed';

export type Role = 'Admin' | 'Approver' | 'Editor' | 'Viewer';

export const ROLES: { role: Role; badge: string; can: string; invite: string }[] = [
    { role: 'Admin', badge: 'admin', can: 'Everything for this client, including settings and access.', invite: 'Everything, including settings, billing and inviting others' },
    { role: 'Editor', badge: 'editor', can: 'Creates announcements, campaigns and pages. Can’t approve or change settings.', invite: 'Website content, campaigns and investor questions. No settings or registry export.' },
    { role: 'Approver', badge: 'approver', can: 'Approves announcement emails and answers to investor questions.', invite: 'Can approve and send announcement emails, plus everything an editor can do' },
    { role: 'Viewer', badge: 'viewer', can: 'Read‑only: registry, reports and analytics.', invite: 'Read‑only. Good for directors who just want to look.' },
];

export interface Person {
    id: string;
    name: string;
    email: string;
    organisation: string;
    role: Role;
    lastActive: string;
    you?: boolean;
}

export interface Invite {
    id: string;
    email: string;
    role: Role;
    invited: string;
}

export const PEOPLE = (company: string): Person[] => [
    { id: 'lf', name: 'Lily Ferliansyah', email: 'lily.ferliansyah@oliveragency.com.au', organisation: 'Oliver Agency', role: 'Admin', lastActive: 'Now', you: true },
    { id: 'wt', name: 'Wallace Turner', email: 'wallace.turner@oliveragency.com.au', organisation: 'Oliver Agency', role: 'Editor', lastActive: '2 hours ago' },
    { id: 'st', name: 'Sam Taylor', email: 's.taylor@49metals.com.au', organisation: company, role: 'Approver', lastActive: 'Yesterday' },
    { id: 'cr', name: 'Cameron Reid', email: 'cameron@reidconsulting.example', organisation: 'Reid Consulting', role: 'Viewer', lastActive: '3 days ago' },
];

export const INVITES: Invite[] = [
    { id: 'editor', email: 'editor@oliveragency.com.au', role: 'Editor', invited: 'invited 2 days ago by Lily Ferliansyah' },
];

export const ACCESS_LOG = [
    { what: 'Vanessa Lim invited as Editor', when: '2 days ago · Lily' },
    { what: 'Cameron Reid changed to Viewer', when: '12 Aug · Lily' },
    { what: 'Sam Taylor added as Approver', when: '3 Mar · Lily' },
];

export const PROVIDERS = ['Automic', 'Computershare', 'Link Market Services', 'Boardroom'];

export const RECENT_SYNCS = [
    { when: '02 Sep · 6:10 pm', ok: true, note: 'OK' },
    { when: '01 Sep · 6:09 pm', ok: true, note: 'OK' },
    { when: '31 Aug · 6:14 pm', ok: true, note: 'OK' },
    { when: '28 Aug · 6:08 pm', ok: true, note: 'OK' },
    { when: '27 Aug · 6:31 pm', ok: false, note: 'Retried once' },
];

export const HOLDERS = 772;

export const PLAN_FEATURES = [
    'Share registry sync with Automic',
    'Automatic ASX announcement emails · unlimited investors',
    'Email builder, campaigns and social posts',
    'All 11 website widgets and the installation checker',
    'Investor questions, sign‑ups and contact forms',
    'Analytics and board reports',
];

export const INVOICES = [
    { id: 'INV‑49M‑2026', date: '1 Sep 2026', amount: 'A$8,900.00' },
    { id: 'INV‑49M‑2025', date: '1 Sep 2025', amount: 'A$8,900.00' },
];

export interface BillingDetails {
    abn: string;
    po: string;
    contact: string;
    email: string;
    address: string;
    suburb: string;
    state: string;
    postcode: string;
    copy: boolean;
    secondEmail: string;
}

export const BILLING: BillingDetails = {
    abn: '12 345 678 901',
    po: '',
    contact: 'Rob Fletcher',
    email: 'accounts@49metals.com.au',
    address: '6, 245 Churchill Avenue',
    suburb: 'Subiaco',
    state: 'WA',
    postcode: '6008',
    copy: true,
    secondEmail: 'cfo@49metals.com.au',
};

// "49 Metals" from "49 Metals Limited"
const shortName = (name: string) => name.replace(/\s+(limited|ltd\.?|pty\.? ltd\.?|nl|inc\.?)$/i, '');

// the current client's identity as the pages print it
export const useClient = () => {
    const stock = useSelector((state: IGlobalState) => state.currentStock);
    const name = stock?.companyName || stock?.ticker || '';
    return {
        stock,
        name,
        short: shortName(name),
        ticker: stock?.ticker ?? '',
        exchange: stock?.exchange ?? '',
    };
};
